import { useState } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Shield, MessageSquare, FlaskConical, AlertTriangle, CheckSquare, HelpCircle, BookOpen, Clock, Stethoscope, Library, CreditCard, Settings, LogOut, Menu, ChevronRight, Crown, Scale, User, Search, ExternalLink } from "lucide-react";

const NAV_ITEMS = [
  { path: "/dashboard/chat", label: "Chat IA", icon: MessageSquare, premium: false },
  { path: "/dashboard/simulator", label: "Simulateur", icon: FlaskConical, premium: false },
  { path: "/dashboard/risks", label: "Classification", icon: AlertTriangle, premium: false },
  { path: "/dashboard/checklist", label: "Checklist", icon: CheckSquare, premium: false },
  { path: "/dashboard/quiz", label: "Quiz", icon: HelpCircle, premium: false },
  { path: "/dashboard/glossary", label: "Glossaire", icon: BookOpen, premium: false },
  { path: "/dashboard/timeline", label: "Timeline", icon: Clock, premium: false },
  { path: "/dashboard/diagnostic", label: "Diagnostic", icon: Stethoscope, premium: false },
  { path: "/dashboard/case-studies", label: "Études de cas", icon: Library, premium: true },
  { path: "/dashboard/search", label: "Recherche", icon: Search, premium: false },
];

function NavLink({ item, active, onClick }) {
  return (
    <button onClick={onClick} data-testid={`nav-${item.path.split("/").pop()}`}
      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-all duration-200 group ${active ? "bg-[var(--ia-navy)] text-white" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"}`}>
      <item.icon className={`h-4 w-4 flex-shrink-0 ${active ? "text-white" : "text-slate-400 group-hover:text-slate-600"}`} />
      <span className="flex-1 text-left truncate">{item.label}</span>
      {item.premium && <Crown className="h-3 w-3 text-amber-500 flex-shrink-0" />}
    </button>
  );
}

function Sidebar({ onNavigate }) {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const handleNav = (path) => {
    navigate(path);
    onNavigate?.();
  };

  return (
    <div className="flex flex-col h-full">
      <div className="p-4 border-b border-[var(--ia-border)]">
        <div className="flex items-center gap-2.5">
          <Shield className="h-7 w-7 text-[var(--ia-navy)]" />
          <div>
            <span className="text-lg font-bold text-[var(--ia-navy)]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>IA Conforme</span>
            <Badge variant="outline" className="ml-2 text-[10px] px-1.5 py-0">{user?.role === "admin" ? "Admin" : user?.role === "premium" ? "Premium" : "Gratuit"}</Badge>
          </div>
        </div>
      </div>
      <ScrollArea className="flex-1 p-3">
        <div className="space-y-1">
          {NAV_ITEMS.map((item) => (
            <NavLink key={item.path} item={item} active={location.pathname === item.path || (item.path === "/dashboard/chat" && location.pathname === "/dashboard")} onClick={() => handleNav(item.path)} />
          ))}
        </div>
        <div className="border-t border-[var(--ia-border)] mt-4 pt-4">
          <NavLink item={{ path: "/dashboard/pricing", label: "Tarifs", icon: CreditCard, premium: false }} active={location.pathname === "/dashboard/pricing"} onClick={() => handleNav("/dashboard/pricing")} />
          {user?.role === "admin" && (
            <NavLink item={{ path: "/dashboard/admin", label: "Administration", icon: Settings, premium: false }} active={location.pathname === "/dashboard/admin"} onClick={() => handleNav("/dashboard/admin")} />
          )}
        </div>
      </ScrollArea>
      <div className="p-3 border-t border-[var(--ia-border)]">
        {user?.role === "free" && (
          <Button variant="outline" className="w-full mb-2 text-xs border-amber-300 text-amber-700 hover:bg-amber-50" onClick={() => handleNav("/dashboard/pricing")} data-testid="upgrade-sidebar-btn">
            <Crown className="h-3.5 w-3.5 mr-1.5" /> Passer en Premium
          </Button>
        )}
        <div className="flex items-center justify-center gap-3 text-[10px] text-[var(--ia-muted)] mb-1.5 flex-wrap">
          <a href="/legal?section=mentions" className="hover:text-[var(--ia-blue)] hover:underline transition-colors">Mentions légales</a>
          <span>·</span>
          <a href="/legal?section=cgu" className="hover:text-[var(--ia-blue)] hover:underline transition-colors">CGU</a>
          <span>·</span>
          <a href="/legal?section=confidentialite" className="hover:text-[var(--ia-blue)] hover:underline transition-colors">Confidentialité</a>
        </div>
        <div className="text-[10px] text-[var(--ia-muted)] text-center">
          © 3H33 SARL — Outil pédagogique
        </div>
      </div>
    </div>
  );
}

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="flex h-screen bg-[var(--ia-surface)]" data-testid="dashboard-layout">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 bg-white border-r border-[var(--ia-border)] flex-col flex-shrink-0">
        <Sidebar />
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <header className="h-14 bg-white border-b border-[var(--ia-border)] flex items-center justify-between px-4 flex-shrink-0">
          <div className="flex items-center gap-3">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="lg:hidden" data-testid="mobile-menu-btn">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-64 p-0">
                <Sidebar onNavigate={() => setMobileOpen(false)} />
              </SheetContent>
            </Sheet>
          </div>
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2 text-sm" data-testid="user-menu-trigger">
                  <div className="w-7 h-7 rounded-full bg-[var(--ia-navy)] flex items-center justify-center text-white text-xs font-medium">
                    {(user?.name || "U")[0].toUpperCase()}
                  </div>
                  <span className="hidden sm:inline text-slate-700">{user?.name}</span>
                  <ChevronRight className="h-3.5 w-3.5 text-slate-400 rotate-90" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <div className="px-2 py-1.5 text-xs text-[var(--ia-muted)]">{user?.email}</div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/dashboard/profile")} data-testid="profile-btn">
                  <User className="h-4 w-4 mr-2" /> Profil & Paramètres
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn" className="text-red-600">
                  <LogOut className="h-4 w-4 mr-2" /> Déconnexion
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
